import { db } from '../../src/firebase.js';
import { auth } from '../../src/firebase.js';
import { doc, getDoc, collection, query, where, getDocs, limit } from "firebase/firestore";
 
export class OrderConfirmation {
    constructor(containerId, orderId) {
        this.containerId = containerId;
        this.orderId = orderId;
        this.el = null;
        this.order = null;
        this.loading = true;
    }
 
    async init() {
        this.el = document.getElementById(this.containerId);
        if (!this.el) return;
 
        this.renderLoading();
        await this.fetchOrder();
        this.loading = false;
        this.render();
    }
 
    async fetchOrder() {
        const maxAttempts = 6;
        const delayMs = 3000;
 
        // Wait for auth to be ready
        const user = await new Promise((resolve) => {
            const unsubscribe = auth.onAuthStateChanged((u) => {
                unsubscribe();
                resolve(u);
            });
        });
 
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                console.log(`[OrderConfirmation] Fetch attempt ${attempt}/${maxAttempts} for sessionId: ${this.orderId}`);
 
                // PRIMARY: Read from Firebase Stripe extension collection
                if (user) {
                    const sessionRef = doc(db, "customers", user.uid, "checkout_sessions", this.orderId);
                    const sessionSnap = await getDoc(sessionRef);
 
                    if (sessionSnap.exists()) {
                        const data = sessionSnap.data();
                        console.log("[OrderConfirmation] Session found in extension collection:", data);
 
                        // Map extension data to our order format
                        const lineItems = data.line_items || [];
                        const shippingDetails = data.shipping_details || data.shipping || {};
                        const shippingAddress = shippingDetails.address || {};
                        const amountTotal = data.amount_total || 0;
                        const shippingOption = (data.shipping_cost && data.shipping_cost.display_name) || 'Standard Delivery';
                        const shippingCostAmt = (data.shipping_cost && data.shipping_cost.amount_total)
                            ? `£${(data.shipping_cost.amount_total / 100).toFixed(2)}`
                            : '£3.99';
 
                        this.order = {
                            items: lineItems.map(item => ({
                                name: item.description || item.price_data?.product_data?.name || 'Item',
                                size: '',
                                quantity: item.quantity || 1,
                                price: `£${((item.amount_total || 0) / 100).toFixed(2)}`
                            })),
                            total: `£${(amountTotal / 100).toFixed(2)}`,
                            customerEmail: data.customer_email || data.customer_details?.email || '',
                            shippingAddress: {
                                line1: shippingAddress.line1 || '',
                                city: shippingAddress.city || '',
                                postalCode: shippingAddress.postal_code || '',
                                country: shippingAddress.country || ''
                            },
                            shippingMethod: shippingOption,
                            shippingCost: shippingCostAmt,
                            status: data.payment_status || 'paid',
                            trackingNumber: null,
                            carrier: null
                        };
                        return;
                    }
                }
 
                // FALLBACK: Check custom orders collection
                const orderRef = doc(db, "orders", this.orderId);
                const orderSnap = await getDoc(orderRef);
                if (orderSnap.exists()) {
                    this.order = orderSnap.data();
                    console.log("[OrderConfirmation] Order found in orders collection:", this.order);
                    return;
                }
 
                // FALLBACK 2: Search orders by session_id field
                const q = query(collection(db, "orders"), where("checkout_session", "==", this.orderId), limit(1));
                const querySnap = await getDocs(q);
                if (!querySnap.empty) {
                    this.order = querySnap.docs[0].data();
                    this.orderId = querySnap.docs[0].id;
                    return;
                }
 
                if (attempt < maxAttempts) {
                    console.log(`[OrderConfirmation] Not found yet, retrying in ${delayMs}ms...`);
                    await new Promise(res => setTimeout(res, delayMs));
                }
 
            } catch (error) {
                console.error(`[OrderConfirmation] Fetch error on attempt ${attempt}:`, error);
                if (attempt === maxAttempts) break;
                await new Promise(res => setTimeout(res, delayMs));
            }
        }
    }
 
    renderLoading() {
        this.el.innerHTML = `
            <div class="fg-order-conf fg-order-conf--loading">
                <div class="fg-order-conf__spinner"></div>
                <p>Retrieving order details...</p>
            </div>
        `;
    }
 
    render() {
        if (!this.order) {
            this.el.innerHTML = `
                <div class="fg-order-conf fg-order-conf--error">
                    <h2>Order not found</h2>
                    <p>We couldn't find the details for reference: <strong>${this.orderId}</strong></p>
                    <button class="fg-btn fg-btn--primary" id="fg-order-conf-shop">Return to Shop</button>
                </div>
            `;
            this.el.querySelector('#fg-order-conf-shop').addEventListener('click', () => {
                window.dispatchEvent(new CustomEvent('fg:navigate', { detail: { page: 'shop' } }));
            });
            return;
        }
 
        const { items, total, shippingAddress, status, trackingNumber, carrier, shippingMethod, shippingCost, customerEmail } = this.order;
        const isShipped = status === 'shipped';
 
        this.el.innerHTML = `
            <div class="fg-order-conf">
                <div class="fg-order-conf__header">
                    <div class="fg-order-conf__success-icon">
                        <svg viewBox="0 0 52 52">
                            <circle class="fg-order-conf__circle" cx="26" cy="26" r="25" fill="none"/>
                            <path class="fg-order-conf__check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
                        </svg>
                    </div>
                    <h1 class="fg-order-conf__title">Order confirmed!</h1>
                    <p class="fg-order-conf__ref">Order reference: <span>${this.orderId}</span></p>
                </div>
 
                <div class="fg-order-conf__body">
                    <section class="fg-order-conf__section">
                        <h3>Delivery Summary</h3>
                        <p style="font-size: 0.9rem; color: var(--text-secondary); margin-bottom: 1rem;">
                            Method: <strong>${shippingMethod || 'Standard Delivery'}</strong> (${shippingCost || '£3.99'})
                        </p>
                        <p style="font-size: 0.9rem; color: var(--text-secondary); margin-bottom: 1rem;">
                            Email: <strong>${customerEmail || 'N/A'}</strong>
                        </p>
                        ${isShipped ? `
                            <div class="fg-order-conf__tracking" style="margin-bottom: 1.5rem; padding: 1.25rem; background: var(--bg-secondary); border-radius: 12px; border: 1px solid var(--border-color);">
                                <p style="margin-bottom: 0.5rem;"><strong>Status:</strong> Shipped</p>
                                <p style="margin-bottom: 0.5rem;"><strong>Carrier:</strong> ${carrier || 'N/A'}</p>
                                <p style="margin: 0;"><strong>Tracking:</strong> ${trackingNumber || 'N/A'}</p>
                            </div>
                        ` : `
                            <p class="fg-order-conf__delivery-time">Estimated delivery: 3-5 working days</p>
                        `}
                        <address class="fg-order-conf__address">
                            ${shippingAddress.line1 || ''}<br>
                            ${shippingAddress.city || ''}, ${shippingAddress.postalCode || ''}<br>
                            ${shippingAddress.country || ''}
                        </address>
                    </section>
 
                    <section class="fg-order-conf__section">
                        <h3>Item Summary</h3>
                        <div class="fg-order-conf__items">
                            ${items.map(item => `
                                <div class="fg-order-conf__item">
                                    <div class="fg-order-conf__item-info">
                                        <span class="fg-order-conf__item-name">${item.name}</span>
                                        <span class="fg-order-conf__item-meta">Qty: ${item.quantity}</span>
                                    </div>
                                    <span class="fg-order-conf__item-price">${item.price}</span>
                                </div>
                            `).join('')}
                        </div>
                        <div class="fg-order-conf__total">
                            <span>Total</span>
                            <span>${total}</span>
                        </div>
                    </section>
                </div>
 
                <div class="fg-order-conf__actions">
                    <button class="fg-btn fg-btn--primary" id="fg-order-conf-shop">Continue Shopping</button>
                    <button class="fg-btn" id="fg-order-conf-invoice">Download Invoice (PDF)</button>
                    <button class="fg-btn" id="fg-order-conf-profile">View Order History</button>
                </div>
            </div>
        `;
 
        this.bindEvents();
    }
 
    bindEvents() {
        this.el.querySelector('#fg-order-conf-shop').addEventListener('click', () => {
            window.dispatchEvent(new CustomEvent('fg:navigate', { detail: { page: 'shop' } }));
        });
 
        this.el.querySelector('#fg-order-conf-profile').addEventListener('click', () => {
            window.dispatchEvent(new CustomEvent('fg:navigate', { detail: { page: 'profile' } }));
        });
 
        this.el.querySelector('#fg-order-conf-invoice').addEventListener('click', () => {
            window.print();
        });
    }
}